import {
  Button,
  Group,
  Modal,
  Stack,
  Title,
  Loader,
  LoadingOverlay,
} from '@mantine/core';
import { IconAlertTriangle, IconCreditCard } from '@tabler/icons-react';
import { primaryColor } from '../../../../constants/theme';
import { useEffect, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from '../../../../components/CheckoutForm';
import { createPaymentIntent } from '../../../../services/PaymentService';
import { useQuery } from '@tanstack/react-query';
import { useApp } from '../../../../context/AppContext';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { BookingResponseModel } from '../../../../types/BookingModels';

export interface BookingPaymentButtonProps {
  booking: BookingResponseModel;
  profileId: string;
  groupedBookings: BookingResponseModel[];
  reloadOnSuccess?: boolean;
}

export const BookingPaymentButton = ({
  profileId,
  booking,
  groupedBookings,
  reloadOnSuccess,
}: BookingPaymentButtonProps) => {
  const isWithinOneHourOfEndTime = useMemo(() => {
    return (
      new Date() <=
      new Date(
        new Date(groupedBookings[groupedBookings.length - 1].endIso).getTime() +
          60 * 60 * 1000
      )
    );
  }, [groupedBookings]);
  const isVisible =
    booking.rate > 0 &&
    booking.paymentStatus === 'pending' &&
    isWithinOneHourOfEndTime &&
    profileId === booking.userId &&
    !booking.isCancelled &&
    !booking.rejected &&
    booking.accepted;
  const { token } = useApp();
  const [paymentModalOpened, setPaymentModalOpened] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [stripePromise, setStripePromise] = useState<Stripe | null>();
  const [clientSecret, setClientSecret] = useState<string | undefined>();
  const { refetch, isLoading, isRefetching, data, isError } = useQuery({
    queryKey: ['create-payment-intent', booking.professionalId, booking.id],
    queryFn: () =>
      createPaymentIntent(
        {
          professionalId: booking.professionalId,
          bookingIds: groupedBookings.map((b) => b.id),
        },
        token ?? ''
      ),
    enabled: isVisible && paymentModalOpened,
    retry: false,
  });

  useEffect(() => {
    if (data?.paymentIntent) {
      setClientSecret(data.paymentIntent);
    }
    if (data?.publishableKey) {
      loadStripe(data.publishableKey).then((stripe) => {
        setStripePromise(stripe);
      });
    }
  }, [data]);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <Modal
        opened={paymentModalOpened}
        onClose={() => {
          console.log('Closing payment modal');
          setPaymentModalOpened(false);
        }}
        size="md"
        closeOnEscape={!isPaymentProcessing}
        closeOnClickOutside={!isPaymentProcessing}
        withCloseButton={!isPaymentProcessing}
      >
        <LoadingOverlay visible={isPaymentProcessing} zIndex={1000} />
        <Stack>
          <Title
            order={3}
            fw="normal"
            ta="center"
            data-testid="payment-modal-title"
          >
            Complete Payment
          </Title>

          {isError ? (
            <PaymentPortalError />
          ) : stripePromise && clientSecret && !isLoading && !isRefetching ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CheckoutForm 
                onPaymentProcessing={setIsPaymentProcessing} 
                reloadOnSuccess={reloadOnSuccess}
              />
            </Elements>
          ) : (
            <LoadingPaymentPortal />
          )}
        </Stack>
        <Group>
          <Button
            variant="outline"
            fullWidth
            onClick={() => setPaymentModalOpened(false)}
            color={primaryColor}
            disabled={isPaymentProcessing}
          >
            CANCEL
          </Button>
        </Group>
      </Modal>
      <Button
        radius="md"
        variant="outline"
        color={primaryColor}
        leftSection={<IconCreditCard size={16} />}
        onClick={() => setPaymentModalOpened(true)}
        data-testid={`pay-button-${booking.id}`}
      >
        Pay
      </Button>
    </>
  );
};

const PaymentPortalError = () => {
  return (
    <Stack align="center" justify="center" gap={15} py={50}>
      <IconAlertTriangle size={60} color={primaryColor} />
      <Title order={4} fw="normal" c="dimmed" ta="center">
        There was an error preparing the payment. Please try again later.
      </Title>
    </Stack>
  );
};

const LoadingPaymentPortal = () => {
  return (
    <Stack
      align="center"
      justify="center"
      gap={15}
      py={50}
      data-testid="payment-loader"
    >
      <Loader size="lg" color={primaryColor} />
      <Title order={4} fw="normal" c="dimmed">
        Preparing Payment...
      </Title>
    </Stack>
  );
};
